/**
 * FOLDER: assets/shoppable-videos.js
 * Shoppable Videos — Palo Alto + GoKwik Cart Drawer Integration (FINAL)
 *
 * ROOT CAUSE OF PREVIOUS BUGS:
 * Calling /cart/add.js directly via fetch() BYPASSES the theme's own
 * ATC interceptor. Palo Alto + GoKwik attach their drawer-open logic
 * to (a) submit events on product forms, and (b) their own internal
 * fetch wrappers. A raw fetch from our code triggers neither.
 *
 * SOLUTION:
 * Instead of calling /cart/add.js directly, we INJECT a hidden product
 * form into the DOM that mirrors what's on a product page, then dispatch
 * a real `submit` event on it. The theme's existing ATC handler picks it
 * up exactly as if the user clicked the native ADD TO CART button — which
 * means: drawer opens, sections refresh, GoKwik syncs, cart updates,
 * everything works automatically.
 *
 * This is the same technique used by every reputable Shopify "Quick Add"
 * app (Vela, Searchanise, etc.) precisely because it's the only approach
 * that survives across themes + checkout extensions.
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────────────────────
     ADD TO CART — inject hidden form, let theme handle it
  ───────────────────────────────────────────────────────────── */
  async function addToCartViaTheme(variantId, btnEl) {
    const vid = parseInt(variantId, 10);
    if (!vid || isNaN(vid)) {
      console.error('[ShoppableVideos] Invalid variant ID:', variantId);
      showToast('Product variant not found.', true);
      return false;
    }

    setButtonLoading(btnEl, true);

    // Try theme-native submit first; fall back to fetch if it fails
    const themeSubmitWorked = await tryThemeFormSubmit(vid);

    if (!themeSubmitWorked) {
      console.log('[ShoppableVideos] Theme form submit unavailable, using fetch fallback');
      await fetchAddToCart(vid);
    }

    setButtonLoading(btnEl, false);
    return true;
  }

  /* ─────────────────────────────────────────────────────────────
     STRATEGY 1: Inject hidden product form, dispatch submit
     This is what makes the theme's drawer actually open.
  ───────────────────────────────────────────────────────────── */
  function tryThemeFormSubmit(variantId) {
    return new Promise((resolve) => {
      try {
        // Remove any leftover hidden form from a previous attempt
        document.getElementById('sv-hidden-atc-form')?.remove();

        // Build a form that LOOKS exactly like a Palo Alto product form.
        // The action, method, classes, and data attrs are what the theme's
        // event delegation listens for.
        const form = document.createElement('form');
        form.id        = 'sv-hidden-atc-form';
        form.action    = '/cart/add';
        form.method    = 'post';
        form.enctype   = 'multipart/form-data';
        form.acceptCharset = 'UTF-8';
        // Palo Alto's selectors for product forms (covers v4/v5/v6):
        form.className = 'product-form product-form--ajax shopify-product-form ajaxcart__form';
        form.setAttribute('data-product-form', '');
        form.setAttribute('data-ajax-cart-form', '');
        form.setAttribute('novalidate', 'novalidate');
        form.style.cssText = 'position:absolute;left:-9999px;top:-9999px;visibility:hidden;pointer-events:none;';

        form.innerHTML =
          '<input type="hidden" name="form_type" value="product">' +
          '<input type="hidden" name="utf8" value="✓">' +
          '<input type="hidden" name="id" value="' + variantId + '">' +
          '<input type="hidden" name="quantity" value="1">' +
          '<button type="submit" name="add" class="product-form__cart-submit btn--add-to-cart">Add</button>';

        document.body.appendChild(form);

        // Did the theme attach a listener? Watch for it via:
        // 1. A `cart:added` / `theme:cart:added` event firing within 4s
        // 2. The /cart.js item count incrementing
        let resolved = false;
        const cleanup = (success) => {
          if (resolved) return;
          resolved = true;
          form.remove();
          resolve(success);
        };

        // Watch for cart-related events that prove the theme picked it up
        const successEvents = [
          'cart:added', 'cart:build', 'cart:open', 'cart:refresh',
          'theme:cart:added', 'theme:cart:open', 'product:added',
          'ajaxProduct.afterAddToCart', 'added.cart',
        ];
        const onSuccess = () => cleanup(true);
        successEvents.forEach((ev) => {
          document.addEventListener(ev, onSuccess, { once: true });
        });

        // Capture initial cart count, then poll for change
        let initialCount = null;
        fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
          .then((r) => r.json())
          .then((c) => { initialCount = c.item_count; })
          .catch(() => {});

        // Submit using the most native mechanism available
        const submitBtn = form.querySelector('button[type="submit"]');

        // Method A: dispatch a real submit event (preferred — themes listen here)
        const submitEvt = new Event('submit', { bubbles: true, cancelable: true });
        const wasHandled = !form.dispatchEvent(submitEvt);

        // Method B: also click the submit button (some themes listen on click)
        if (submitBtn && !wasHandled) {
          submitBtn.click();
        }

        // Poll cart count for 4 seconds. If it goes up, theme handled it.
        let polls = 0;
        const pollInterval = setInterval(async () => {
          polls++;
          if (polls > 16 || resolved) {
            clearInterval(pollInterval);
            if (!resolved) cleanup(false);
            return;
          }
          if (initialCount === null) return;
          try {
            const r = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
            const c = await r.json();
            if (c.item_count > initialCount) {
              clearInterval(pollInterval);
              updateCartBubble(c.item_count);
              showToast('Added to cart!', false);
              closePopup();
              setTimeout(() => openCartDrawer(), 150);
              cleanup(true);
            }
          } catch (_) {}
        }, 250);

      } catch (err) {
        console.warn('[ShoppableVideos] Theme form submit threw:', err);
        resolve(false);
      }
    });
  }

  /* ─────────────────────────────────────────────────────────────
     STRATEGY 2: Fallback fetch + manual drawer trigger
     Only runs if the theme didn't pick up the form submit.
  ───────────────────────────────────────────────────────────── */
  async function fetchAddToCart(variantId) {
    try {
      const fd = new FormData();
      fd.append('id', String(variantId));
      fd.append('quantity', '1');
      fd.append('form_type', 'product');

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Accept': 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: fd,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || err.message || 'Add to cart failed');
      }

      const cart = await fetch('/cart.js', {
        headers: { 'Accept': 'application/json' },
      }).then((r) => r.json());

      updateCartBubble(cart.item_count);
      notifyAllListeners(cart);

      closePopup();
      setTimeout(() => openCartDrawer(), 200);
      showToast('Added to cart!', false);
      return true;
    } catch (err) {
      console.error('[ShoppableVideos] Fetch ATC failed:', err);
      showToast(err.message || 'Could not add to cart.', true);
      return false;
    }
  }

  /* ─────────────────────────────────────────────────────────────
     OPEN CART DRAWER — broadcast all known events + click cart icon
  ───────────────────────────────────────────────────────────── */
  function openCartDrawer() {
    if (isCartDrawerVisible()) return;

    // Broadcast every possible drawer-open event
    const events = [
      'theme:cart:open', 'theme:cart:added', 'theme:cart:change',
      'cart:open', 'cart:refresh', 'cart:build', 'cart:rerender',
      'product:added', 'ajaxProduct:added',
      'added.cart', 'cart.requestComplete',
    ];
    [document, document.body, window].forEach((tgt) => {
      events.forEach((name) => {
        try { tgt.dispatchEvent(new CustomEvent(name, { bubbles: true })); }
        catch (_) {}
      });
    });

    // jQuery triggers (Palo Alto v4/v5 use jQuery internally)
    if (window.jQuery) {
      try {
        window.jQuery(document).trigger('ajaxProduct.afterAddToCart');
        window.jQuery(document.body).trigger('added.cart');
        window.jQuery(document).trigger('cart.requestComplete');
      } catch (_) {}
    }

    // Try direct API calls
    [
      () => window.theme?.cart?.open?.(),
      () => window.theme?.cartDrawer?.open?.(),
      () => window.theme?.AjaxCart?.open?.(),
      () => window.CartDrawer?.open?.(),
      () => window.cartDrawer?.open?.(),
      () => window.gokwikSdk?.openCart?.(),
      () => window.GoKwik?.openCart?.(),
    ].forEach((fn) => { try { fn(); } catch (_) {} });

    // Synthesise a click on the header cart icon — this is the
    // user-equivalent action and is guaranteed to work if the icon exists.
    setTimeout(() => {
      if (isCartDrawerVisible()) return;

      const selectors = [
        // Palo Alto specific
        '[data-action="open-drawer"][data-drawer-id="ajax-cart"]',
        '[data-action="open-drawer"][data-drawer-id="CartDrawer"]',
        '[data-drawer-toggle="ajax-cart"]',
        '.js-drawer-open-cart',
        // Header cart icon links/buttons (most likely match for Market99)
        'a.site-header__cart',
        'a[href="/cart"].site-header__icon',
        '.site-header__icon[href="/cart"]',
        '.site-header__icons a[href="/cart"]',
        '.header__icon--cart',
        '[data-cart-toggle]',
        '[data-js-cart-open]',
        'header a[href="/cart"]',
        'header button[aria-label*="cart" i]',
        'a[href="/cart"]',
      ];

      for (const sel of selectors) {
        const el = document.querySelector(sel);
        if (!el) continue;
        // Real-coordinate MouseEvent — themes that filter untrusted events still react
        const rect = el.getBoundingClientRect();
        el.dispatchEvent(new MouseEvent('click', {
          bubbles: true, cancelable: true, view: window,
          clientX: rect.left + rect.width / 2,
          clientY: rect.top + rect.height / 2,
          button: 0,
        }));
        el.click?.();
        console.log('[ShoppableVideos] Drawer triggered via click on:', sel);
        return;
      }

      console.warn('[ShoppableVideos] No drawer trigger found on page.');
    }, 200);
  }

  function isCartDrawerVisible() {
    const sels = [
      '#ajax-cart.is-active', '#ajax-cart.is-open',
      '#CartDrawer.is-active', '#CartDrawer.active',
      '.cart-drawer.is-open', '.drawer--cart.is-open',
      '[data-drawer="ajax-cart"].is-active',
      'body.drawer-open', 'body.js-drawer-open-cart',
    ];
    return sels.some((s) => document.querySelector(s));
  }

  /* ─────────────────────────────────────────────────────────────
     NOTIFY all listeners (theme + GoKwik)
  ───────────────────────────────────────────────────────────── */
  function notifyAllListeners(cartData) {
    try {
      const detail = { cart: cartData };
      [document, document.body, window].forEach((tgt) => {
        ['cart:updated', 'cart:refresh', 'cart:build',
         'gk:cart:updated', 'theme:cart:change'].forEach((ev) => {
          tgt.dispatchEvent(new CustomEvent(ev, { bubbles: true, detail }));
        });
      });
      window.gokwikSdk?.refreshCart?.();
      window.GoKwik?.updateCart?.(cartData);
    } catch (_) {}
  }

  /* ─────────────────────────────────────────────────────────────
     CART BUBBLE (only used as backup; theme usually does this itself)
  ───────────────────────────────────────────────────────────── */
  function updateCartBubble(count) {
    const selectors = [
      '#CartCount', '.cart-count', '.cart-link__bubble-num',
      '[data-cart-count]', '[data-cart-item-count]',
      '.site-header__cart-count', '.header__cart-count',
      '.cart-count-bubble',
    ];
    selectors.forEach((sel) => {
      document.querySelectorAll(sel).forEach((el) => {
        el.textContent = count;
        el.setAttribute('data-count', count);
        if (count > 0) {
          el.removeAttribute('hidden');
          el.classList.add('cart-link--has-items');
          el.style.display = '';
        }
      });
    });
  }

  /* ─────────────────────────────────────────────────────────────
     UI HELPERS
  ───────────────────────────────────────────────────────────── */
  function setButtonLoading(btn, isLoading) {
    if (!btn) return;
    const text = btn.querySelector('.sv-atc-text');
    const load = btn.querySelector('.sv-atc-loading');
    btn.disabled = isLoading;
    if (text) text.style.display = isLoading ? 'none' : '';
    if (load) load.style.display = isLoading ? 'flex' : 'none';
  }

  function showToast(msg, isError) {
    const toast = document.getElementById('sv-toast');
    const msgEl = document.getElementById('sv-toast-msg');
    if (!toast) return;
    if (msgEl) msgEl.textContent = msg;
    toast.classList.toggle('is-error', !!isError);
    toast.classList.add('is-visible');
    setTimeout(() => toast.classList.remove('is-visible'), 3000);
  }

  /* ─────────────────────────────────────────────────────────────
     VIDEO BEHAVIOR
  ───────────────────────────────────────────────────────────── */
  function initVideoObserver() {
    const videos = document.querySelectorAll('.sv-video');
    if (!videos.length) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const v = entry.target;
        const w = v.closest('.sv-video-wrapper');
        if (entry.isIntersecting) {
          v.play().catch(() => {});
          w?.classList.remove('is-paused');
        } else {
          v.pause();
        }
      });
    }, { threshold: 0.5 });
    videos.forEach((v) => observer.observe(v));
  }

  function initMuteButtons() {
    document.querySelectorAll('.sv-mute-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const v = btn.closest('.sv-reel-item')?.querySelector('.sv-video');
        if (!v) return;
        v.muted = !v.muted;
        btn.querySelector('.sv-icon-muted').style.display   = v.muted ? '' : 'none';
        btn.querySelector('.sv-icon-unmuted').style.display = v.muted ? 'none' : '';
      });
    });
  }

  function initTapToPause() {
    document.querySelectorAll('.sv-video-wrapper').forEach((wrapper) => {
      wrapper.addEventListener('click', (e) => {
        if (e.target.closest('.sv-mute-btn') ||
            e.target.closest('.sv-product-preview') ||
            e.target.closest('.sv-shop-btn')) return;
        const v = wrapper.querySelector('.sv-video');
        if (!v) return;
        if (v.paused) {
          v.play().catch(() => {});
          wrapper.classList.remove('is-paused');
        } else {
          v.pause();
          wrapper.classList.add('is-paused');
        }
      });
    });
  }

  function initNavArrows() {
    document.querySelectorAll('.sv-reel-container').forEach((container) => {
      const track = container.querySelector('.sv-reel-track');
      const prev  = container.querySelector('.sv-nav--prev');
      const next  = container.querySelector('.sv-nav--next');
      if (!track || !prev || !next) return;
      const cardWidth = () => {
        const card = track.querySelector('.sv-reel-item');
        return card ? card.offsetWidth + 10 : 260;
      };
      prev.addEventListener('click', () => track.scrollBy({ left: -cardWidth() * 2, behavior: 'smooth' }));
      next.addEventListener('click', () => track.scrollBy({ left:  cardWidth() * 2, behavior: 'smooth' }));
      const updateArrows = () => {
        prev.style.opacity = track.scrollLeft > 8 ? '1' : '0.35';
        next.style.opacity = track.scrollLeft < track.scrollWidth - track.clientWidth - 8 ? '1' : '0.35';
      };
      track.addEventListener('scroll', updateArrows, { passive: true });
      updateArrows();
    });
  }

  /* ─────────────────────────────────────────────────────────────
     POPUP
  ───────────────────────────────────────────────────────────── */
  const overlay  = document.getElementById('sv-popup-overlay');
  const popImg   = document.getElementById('sv-popup-image');
  const popTitle = document.getElementById('sv-popup-title');
  const popPrice = document.getElementById('sv-popup-pricing');
  const atcBtn   = document.getElementById('sv-atc-btn');
  const viewBtn  = document.getElementById('sv-view-btn');

  function openPopup(blockId) {
    if (!overlay || !blockId) return;
    const dataNode = document.getElementById('sv-pd-' + blockId);
    if (!dataNode) {
      console.error('[ShoppableVideos] Popup data node missing:', blockId);
      return;
    }

    const title   = dataNode.dataset.title   || '';
    const image   = dataNode.dataset.image   || '';
    const url     = dataNode.dataset.url     || '#';
    const variant = dataNode.dataset.variant || '';

    const priceEl   = dataNode.querySelector('.sv-pd-price');
    const compareEl = dataNode.querySelector('.sv-pd-compare');

    if (popImg)   { popImg.src = image; popImg.alt = title; }
    if (popTitle) popTitle.textContent = title;
    if (viewBtn)  { viewBtn.href = url; viewBtn.dataset.href = url; }
    if (atcBtn)   atcBtn.dataset.variantId = variant;

    let html = '';
    if (priceEl)   html += '<span class="sv-popup-price-now">'  + priceEl.textContent.trim()   + '</span>';
    if (compareEl) html += '<span class="sv-popup-price-was">'  + compareEl.textContent.trim() + '</span>';
    if (popPrice)  popPrice.innerHTML = html;

    overlay.classList.add('is-open');
    overlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('sv-popup-open');
    setTimeout(() => atcBtn?.focus(), 320);
  }

  function closePopup() {
    if (!overlay) return;
    overlay.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('sv-popup-open');
  }

  function initPopup() {
    document.querySelectorAll('.sv-product-preview, .sv-shop-btn').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        const item    = el.closest('.sv-reel-item');
        const blockId = item?.dataset.blockId;
        if (!blockId) return;
        openPopup(blockId);
      });
    });

    if (viewBtn) {
      viewBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const href = viewBtn.dataset.href || viewBtn.getAttribute('href');
        if (href && href !== '#') window.location.href = href;
      });
    }

    if (atcBtn) {
      atcBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const variantId = atcBtn.dataset.variantId;
        if (!variantId) {
          showToast('No variant found for this product.', true);
          return;
        }
        addToCartViaTheme(variantId, atcBtn);
      });
    }

    overlay?.addEventListener('click', (e) => {
      if (e.target === overlay) closePopup();
    });

    document.querySelector('.sv-popup-close')?.addEventListener('click', closePopup);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && overlay?.classList.contains('is-open')) closePopup();
    });

    let touchStartY = 0;
    const popup = overlay?.querySelector('.sv-popup');
    if (popup) {
      popup.addEventListener('touchstart', (e) => {
        touchStartY = e.touches[0].clientY;
      }, { passive: true });
      popup.addEventListener('touchend', (e) => {
        if (e.changedTouches[0].clientY - touchStartY > 80) closePopup();
      }, { passive: true });
    }
  }

  function initTouchSwipe() {
    document.querySelectorAll('.sv-reel-track').forEach((track) => {
      let sx = 0;
      track.addEventListener('touchstart', (e) => { sx = e.touches[0].clientX; }, { passive: true });
      track.addEventListener('touchend', (e) => {
        const diff = sx - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 40) {
          const card = track.querySelector('.sv-reel-item');
          const w    = card ? card.offsetWidth + 10 : 260;
          track.scrollBy({ left: diff > 0 ? w : -w, behavior: 'smooth' });
        }
      }, { passive: true });
    });
  }

  function init() {
    initVideoObserver();
    initMuteButtons();
    initTapToPause();
    initNavArrows();
    initPopup();
    initTouchSwipe();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);

})();
